<template>
<v-container fluid fill-height grid-list-md>
    <v-layout v-if="loading" align-center justify-center>
        <v-progress-circular indeterminate></v-progress-circular>
    </v-layout>
    <v-layout v-else column>        
        <v-card>
            <v-toolbar color="primary" class="white--text">
                <v-icon @click="$router.push({ name: 'trapinspection' })" large dark>arrow_back</v-icon>
                <v-toolbar-title>Trap Inspection History</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-icon large dark>history</v-icon>
            </v-toolbar>
        </v-card>
        <v-card flat>
            <v-layout row wrap pa-2>
                <v-flex>
                    <label-value :label="'Trap Identifier'" :value="trap.TrapIdentifier"></label-value>
                </v-flex>
                <v-flex>
                    <label-value :label="'Trap Type'" :value="trap.TrapType"></label-value>
                </v-flex>
                <v-flex>
                    <label-value :label="'Zone'" :value="trap.TrapZoneName"></label-value>
                </v-flex>
                <v-flex>
                    <label-value :label="'Date Placed'" :value="trap.DatePlaced | formatDate"></label-value>
                </v-flex>
                <v-flex>
                    <label-value :label="'Location'" :value="trap.LocationName"></label-value>
                </v-flex>
            </v-layout>
        </v-card>
        <v-card dark color="shrink grey darken-1" flat>
            <v-card-title>
                <span class="title">Inspections</span>
                <v-spacer />
                <v-icon large>search</v-icon>
            </v-card-title>
        </v-card>        
        <v-data-table
            :headers="headers"
            :items="inspections"
            :rows-per-page-items="rowsPerPageItems"
            :pagination.sync="pagination"
            :loading="tableLoading"
            >
            <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
            <template slot="items" slot-scope="props">
                <td>{{ props.item.InspectionDate | formatDateTime }}</td>
                <td>{{props.item.BugCount}}</td>
                <td><span class="clamp-text">{{props.item.Notes}}</span></td>
                <td>
                    <v-layout row>
                        <v-flex shrink>
                            <v-btn color="primary" small block @click="edit(props.item)"><v-icon>edit</v-icon></v-btn>
                        </v-flex>                            
                        <v-flex shrink>
                            <v-btn color="error" small block @click="showDeleteDialog(props.item)"><v-icon>delete</v-icon></v-btn>
                        </v-flex>
                    </v-layout>
                </td>
            </template>
            <template v-slot:footer>
                <td :colspan="headers.length">
                    <v-btn color="info" dark @click="loadMoreInspections(trap.TrapID)">Load More</v-btn>
                </td>
            </template>
        </v-data-table>
    </v-layout>
    <v-dialog v-model="showAddEdit" fullscreen hide-overlay transition="dialog-bottom-transition" @keydown.esc="cancelEdit">
        <v-card>            
            <trap-inspection-edit :inspection="editItem" @cancel="cancelEdit" @save="saveItem"></trap-inspection-edit>
        </v-card>
    </v-dialog>
    <delete-dialog
        :DisplayDialog="showConfirmDeleteImage" 
        :DialogTitle="'Delete Trap Inspection'" 
        :DialogContents="'Are you sure you want to delete this trap inspection?'"
        @No="showConfirmDeleteImage = false"
        @Yes="deleteTrapInspection(deleteItem)">
    </delete-dialog>
</v-container>
</template>

<script>
import LabelValue from '@/components/generic/LabelValue.vue';
import TrapInspectionEdit from '@/components/trap/TrapInspectionEdit.vue';
import DeleteDialog from "@/components/generic/YesNoPopup.vue";

export default {
    components: {
        LabelValue,
        TrapInspectionEdit,
        DeleteDialog
    },    
    data(){
        return {
            showConfirmDeleteImage: false,
            deleteItem: null,
            loading: false,
            tableLoading: false,
            showAddEdit: false,
            editItem: null,
            pagination: {
                page: 1,
                rowsPerPage: 25,
                descending: false
            },
            rowsPerPageItems: [10, 25, 50, 100],
            headers: [
                {
                    text: "Inspection Date",
                    value: "InspectionDate",
                    align: 'left'
                },
                {
                    text: "Count",
                    value: "BugCount",
                    align: 'left'
                },
                {
                    text: "Notes",
                    value: "Notes",
                    align: 'left'
                },                              
                {
                    text: "Actions",
                    value: "",
                    align: "left",
                    sortable: false,
                    width:"150px"                    
                }
            ]
        };
    },
    computed: {
        inspections() {
            return this.$store.getters["TrapInspectionStore/getInspections"](parseInt(this.$route.params.trapID)).filter(i => !i.Deleted);
        },
        trap() {
            return this.$store.getters["TrapInspectionStore/getTrap"](parseInt(this.$route.params.trapID));
        }
    },
    methods: {
        async loadMoreInspections(trapID) {
            this.tableLoading = true;

            await this.$store.dispatch("TrapInspectionStore/loadMoreTrapInspectionsForTrap", trapID);

            this.tableLoading = false;
        },
        async edit(item) {
            // These are inspection details already linked to the inspection
            let details = this.$store.getters["TrapInspectionStore/getInspectionsByID"](item.TrapInspectionID);
            let filteredLocationsByProductCategory = this.$store.getters["PestAndDiseaseStore/getFilteredLocations"];

            // Let's now add any missing traps that weren't part of this inspection
            const traps = this.$store.getters["TrapInspectionStore/getTraps"](this.trap.PlantID, this.trap.DivisionID, this.trap.LocationID, filteredLocationsByProductCategory);

            traps.forEach(trap => {
                if (details.every(d => d.TrapID !== trap.TrapID)) {
                    details.push({ TrapModel: trap });
                }
            });

            this.editItem = {
                TrapInspectionID: item.TrapInspectionID,
                LocationID: item.LocationID,
                LocationName: item.LocationName,
                InspectionDate: item.InspectionDate,
                InspectionNotes: item.InspectionNotes,
                Details: details
            }

            this.showAddEdit = true;
        },
        async cancelEdit() {
            this.showAddEdit = false;
            this.editItem = null;
        },
        async saveItem(item) {
            this.showAddEdit = false;
            this.tableLoading = true;            

            await this.$store.dispatch("TrapInspectionStore/saveTrapInspection", item);
                    
            this.tableLoading = false;
            this.editItem = null;
        },
        async deleteTrapInspection(trapInspection) {
            trapInspection.Deleted = true;
            trapInspection.Details = this.$store.getters["TrapInspectionStore/getInspectionsByID"](trapInspection.TrapInspectionID);
            this.showConfirmDeleteImage = false;            

            await this.$store.dispatch("TrapInspectionStore/saveTrapInspection", trapInspection);
            
            this.deleteItem = null;
        },
        showDeleteDialog(trapInspection) {
            this.deleteItem = trapInspection;
            this.showConfirmDeleteImage = true;
        }
    },
    async created() {
        if (!this.trap) {
            this.loading = true;

            await this.$store.dispatch("TrapInspectionStore/loadTrapsForLocationByTrap", parseInt(this.$route.params.trapID));
            await this.$store.dispatch("TrapInspectionStore/loadTrapInspectionsForTrap", parseInt(this.$route.params.trapID));


            this.loading = false;
        }
    }
}

</script>
<style>
.clamp-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
    text-overflow: ellipsis;
    -webkit-line-clamp: 3;
}
</style>